import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { SMLPageHead } from '../components/SMLPageHead/SMLPageHead';
import { CombinedPlaylistReachPanel } from '../components/CombinedPlaylistReachPanel/CombinedPlaylistReachPanel';
import { FollowingTiles } from '../components/FollowingTiles/FollowingTiles';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useSMLDatePickerDateParam } from '../hooks/useSMLDatePickerDateParam';
import { SMLDatePicker } from '../components/SMLDatePicker/SMLDatePicker';
import { smlNavigationGroupMessages, smlNavigationMessages } from '../navigation/smlNavigationMessages';
import { WatchlistTrendsRow } from './WatchlistTrendsRow';
import { WatchlistFirstStep } from './WatchlistFirstStep';
import { WatchlistMarketsRow } from './WatchlistMarketsRow';
import { WatchlistPlaylistsPanel } from './WatchlistPlaylistsPanel';
import { WatchlistTracksPanel } from './WatchlistTracksPanel';
import { WatchlistArtistsPanel } from './WatchlistArtistsPanel';
import { WATCHLIST_PAGE_SIZE } from './watchlistPageSize';

/**
 * Public intelligence → Watchlist (design: music24-artist-home f1). The old Home, moved as it was:
 * playlist reach + following tiles, trends, markets and the three followed lists. The setup dock
 * stays on the artist Home.
 */
const SMLWatchlistViewComponent: React.FC = () => {
  const intl = useIntl();
  const trpcMusicAPI = useTRPCAPI();
  const { selectedDate, selectedDateValue, rangeDays } = useSMLDatePickerDateParam();

  const breadcrumbs = useMemo(
    () => [
      { name: intl.formatMessage(smlNavigationGroupMessages.publicName) },
      { name: intl.formatMessage(smlNavigationMessages.watchlist) },
    ],
    [intl]
  );

  // The first page of each list. The panels read the same first page, so the cache answers both.
  const firstPage = { offset: 0, limit: WATCHLIST_PAGE_SIZE };
  const { data: playlistsData } = useTRPCQuery(trpcMusicAPI.playlists.listFollowed.queryOptions(firstPage));
  const { data: artistsData } = useTRPCQuery(trpcMusicAPI.artists.listFollowed.queryOptions(firstPage));
  const { data: tracksData } = useTRPCQuery(trpcMusicAPI.tracks.listFollowed.queryOptions(firstPage));
  // With no follow at all, the trends cards show tracks of other people: show the first step.
  const followsNothing =
    playlistsData !== undefined &&
    artistsData !== undefined &&
    tracksData !== undefined &&
    playlistsData.total + artistsData.total + tracksData.total === 0;

  return (
    <SMLContainer
      header={
        <SMLPageHead
          breadcrumbs={breadcrumbs}
          title={intl.formatMessage({
            id: 'app.sml.dashboard.header.title',
            defaultMessage: 'Your watchlist, read in real time',
          })}
          subtitle={intl.formatMessage({
            id: 'app.sml.dashboard.header.subtitle',
            defaultMessage:
              'Where your followed artists, tracks and playlists stand today — and what moved while you were away.',
          })}
        >
          <div className="d-flex flex-column align-items-end gap-1">
            <SMLDatePicker />
            <span className="text-muted small">
              {intl.formatMessage(
                { id: 'app.sml.home.dataAsOf', defaultMessage: 'Data as of {date}' },
                { date: intl.formatDate(selectedDate, { dateStyle: 'medium' }) }
              )}
            </span>
          </div>
        </SMLPageHead>
      }
    >
      <Row className="mb-4 g-3">
        <Col xs={12} lg={8}>
          <CombinedPlaylistReachPanel endDate={selectedDate} />
        </Col>
        <Col xs={12} lg={4}>
          <FollowingTiles
            artistsTotal={artistsData?.total ?? 0}
            tracksTotal={tracksData?.total ?? 0}
            playlistsTotal={playlistsData?.total ?? 0}
            dateValue={selectedDateValue}
          />
        </Col>
      </Row>

      {followsNothing ? <WatchlistFirstStep /> : <WatchlistTrendsRow />}
      <WatchlistMarketsRow date={selectedDate} rangeDays={rangeDays} />

      <WatchlistPlaylistsPanel selectedDateValue={selectedDateValue} />
      <WatchlistTracksPanel selectedDate={selectedDate} selectedDateValue={selectedDateValue} />
      <WatchlistArtistsPanel selectedDate={selectedDate} selectedDateValue={selectedDateValue} />
    </SMLContainer>
  );
};

export const SMLWatchlistView = TypedMemo(SMLWatchlistViewComponent);
